import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";
import { devWarn } from "./dev-log";

const BrandCtx = createContext({ tenant: null, loading: true, refresh: () => {} });

/**
 * Detect a tenant from:
 *  1. ?tenant=<sub>   (preview / testing fallback)
 *  2. window.location.host  e.g. acme.bizpulseai.net
 */
function detectTenantHint() {
  if (typeof window === "undefined") return { host: "", subdomain: "" };
  const params = new URLSearchParams(window.location.search);
  const qs = params.get("tenant");
  if (qs) {
    try { sessionStorage.setItem("bp_tenant_override", qs); } catch (err) { devWarn("[brand] sessionStorage write failed", err); }
    return { host: "", subdomain: qs };
  }
  let stored = "";
  try { stored = sessionStorage.getItem("bp_tenant_override") || ""; } catch (err) { devWarn("[brand] sessionStorage read failed", err); }
  if (stored) return { host: "", subdomain: stored };
  return { host: window.location.host, subdomain: "" };
}

function applyBrandVars(tenant) {
  const root = document.documentElement;
  // Clear previous
  root.style.removeProperty("--brand-primary");
  root.style.removeProperty("--brand-accent");
  if (!tenant) return;
  if (tenant.brand_primary) root.style.setProperty("--brand-primary", tenant.brand_primary);
  if (tenant.brand_accent) root.style.setProperty("--brand-accent", tenant.brand_accent);
}

export const BrandProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const hint = detectTenantHint();
    if (!hint.host && !hint.subdomain) { setTenant(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/brand/by-host", { params: hint });
      setTenant(data.tenant || null);
      applyBrandVars(data.tenant);
    } catch (err) {
      devWarn("[brand] /brand/by-host lookup failed", err);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({ tenant, loading, refresh }), [tenant, loading]);
  return <BrandCtx.Provider value={value}>{children}</BrandCtx.Provider>;
};

export const useBrand = () => useContext(BrandCtx);
